import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { Moon, Sun } from 'lucide-react';

/**
 * Common theme switch — sun & moon inside the pill, knob slides between them.
 * Default: light.
 */
export default function ThemeToggle({ className = '' }) {
  const { setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!isDark}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      data-testid="theme-toggle"
      disabled={!mounted}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className={`
        relative inline-flex h-8 w-14 shrink-0 items-center rounded-full
        border transition-colors duration-200 cursor-pointer overflow-hidden
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40
        disabled:cursor-default disabled:opacity-70
        ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-amber-100 border-amber-200'}
        ${className}
      `}
    >
      {/* Icons stay inside the switch box */}
      <span className="absolute inset-0 z-0 flex items-center justify-between px-[7px] pointer-events-none">
        <Sun className={`h-3.5 w-3.5 ${isDark ? 'text-slate-500' : 'text-amber-500'}`} />
        <Moon className={`h-3.5 w-3.5 ${isDark ? 'text-indigo-200' : 'text-slate-400'}`} />
      </span>

      {/* Knob slides left (light) / right (dark) */}
      <span
        aria-hidden
        className={`
          relative z-10 block h-6 w-6 rounded-full bg-white shadow-md
          transition-transform duration-200 ease-out
          ${isDark ? 'translate-x-[1.85rem]' : 'translate-x-1'}
        `}
      />
    </button>
  );
}
